import { ACCEPTED_BULK_EXTENSIONS, HIGH_CONFIDENCE_THRESHOLD, MAX_BULK_FILE_SIZE_BYTES, POTENTIAL_MATCH_THRESHOLD } from "./constants";
import type { BulkMatchRow, ConsolidatedCompany, MatchStatus, MatchSummary, ResultFilters, ReviewState } from "./types";

export const isRowUnresolved = (row: BulkMatchRow) => !row.matched_company && !row.confidence && !row.candidates?.length;

export const getMatchStatus = (confidence: number): MatchStatus => {
  if (confidence >= HIGH_CONFIDENCE_THRESHOLD) return "HIGH_CONFIDENCE";
  if (confidence >= POTENTIAL_MATCH_THRESHOLD) return "POTENTIAL_MATCH";
  if (confidence > 0) return "LOW_CONFIDENCE";
  return "NO_MATCH";
};

export const isAutoMatched = (row: BulkMatchRow) => getMatchStatus(row.confidence) === "HIGH_CONFIDENCE";

export const needsReview = (row: BulkMatchRow) => {
  const status = getMatchStatus(row.confidence);
  return status === "POTENTIAL_MATCH" || status === "LOW_CONFIDENCE";
};

export const hasNoMatch = (row: BulkMatchRow) => !row.matched_company || getMatchStatus(row.confidence) === "NO_MATCH";

const CHIP_CLASSES: Record<MatchStatus, string> = {
  HIGH_CONFIDENCE: "tw-bg-success-100 tw-border-success-600 tw-text-confidence-high-text",
  POTENTIAL_MATCH: "tw-bg-confidence-potential-bg tw-border-semantic-yellow-900 tw-text-confidence-potential-text",
  LOW_CONFIDENCE: "tw-bg-confidence-low-bg tw-border-confidence-low-border tw-text-confidence-potential-text",
  NO_MATCH: "tw-bg-gray-100 tw-border-gray-300 tw-text-gray-600",
};

export const getConfidenceChipClasses = (confidence: number) => CHIP_CLASSES[getMatchStatus(confidence)];

const ROW_TINTS: Record<MatchStatus, string> = {
  HIGH_CONFIDENCE: "",
  POTENTIAL_MATCH: "tw-bg-confidence-potential-row",
  LOW_CONFIDENCE: "tw-bg-confidence-low-row",
  NO_MATCH: "tw-bg-confidence-nomatch-row",
};

export const getMatchedCellTint = (confidence: number) => ROW_TINTS[getMatchStatus(confidence)];

export const buildMatchSummary = (rows: BulkMatchRow[]): MatchSummary =>
  rows.reduce<MatchSummary>(
    (acc, row) => {
      if (hasNoMatch(row)) acc.no_match += 1;
      else if (isAutoMatched(row) || row.is_reviewed) acc.auto_matched += 1;
      else acc.needs_review += 1;
      return acc;
    },
    { auto_matched: 0, needs_review: 0, no_match: 0 }
  );

export const getUnreviewedRows = (rows: BulkMatchRow[]) => rows.filter((row) => !hasNoMatch(row) && needsReview(row) && !row.is_reviewed);

export const getReviewState = (row: BulkMatchRow): ReviewState => {
  if (row.is_reviewed) return "REVIEWED";
  if (needsReview(row)) return "NEEDS_REVIEW";
  return "NO_REVIEW_REQUIRED";
};

export const DEFAULT_RESULT_FILTERS: ResultFilters = { confidence: [], review: [] };

export const countActiveFilters = (filters: ResultFilters) => filters.confidence.length + filters.review.length;

export const getCompanyLei = (company: ConsolidatedCompany) => (company.lei ? String(company.lei) : "");

export const applyConsolidatedSearch = (companies: ConsolidatedCompany[], query: string) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return companies;
  return companies.filter((company) => (company.key ?? "").toLowerCase().includes(needle) || getCompanyLei(company).toLowerCase().includes(needle));
};

export const applyResultFilters = (rows: BulkMatchRow[], filters: ResultFilters) =>
  rows.filter((row) => {
    if (filters.confidence.length && !filters.confidence.includes(getMatchStatus(row.confidence))) return false;
    if (filters.review.length && !filters.review.includes(getReviewState(row))) return false;
    return true;
  });

export function toggleFilterValue<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

export const getFileExtension = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex === -1 ? "" : fileName.slice(dotIndex).toLowerCase();
};

export const isAcceptedFileType = (file: File) => ACCEPTED_BULK_EXTENSIONS.includes(getFileExtension(file.name));

export const isWithinSizeLimit = (file: File) => file.size <= MAX_BULK_FILE_SIZE_BYTES;

export const formatFileSize = (bytes: number) => {
  if (!bytes) return "0KB";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
};

export const formatSecondsLeft = (seconds: number | null) => {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) return `${Math.ceil(seconds)} sec left`;
  return `${Math.ceil(seconds / 60)} min left`;
};

export const formatShipmentCount = (count?: number) => {
  if (!count || count < 0) return "";
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, "")}M shipments`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1).replace(/\.0$/, "")}K shipments`;
  return `${count} shipment${count === 1 ? "" : "s"}`;
};
